import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from '../_models/subscription';
import { SubscriptionService } from '../_services/subscription.service';
import { faCircleArrowLeft, faHomeAlt, faPlusCircle, faPenToSquare, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { environment } from '../../environments/environment';
import { Property } from '../_models/property';
import { PropertyService } from '../_services/property.service';
import { AuthenticationService } from '../_services/authentication.service';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-subscription',
    templateUrl: './subscription.component.html',
    styleUrls: ['./subscription.component.css'],
    standalone: false
})
export class SubscriptionComponent implements OnInit {
    faCircleArrowLeft = faCircleArrowLeft;
    faHomeAlt = faHomeAlt;
    faPlusCircle = faPlusCircle;
    faPenToSquare = faPenToSquare;
    faTimesCircle = faTimesCircle;

    loading = false;
    errorMessage = '';
    propertyId: number | null = null;
    selectedProperty: Property | null = null;
    subscriptions: Subscription[] = [];
    subscriptionForm: FormGroup;
    editingSubscriptionId: number | null = null;
    creatingSubscription = false;
    saving = false;

    constructor(
        private subscriptionService: SubscriptionService,
        private propertyService: PropertyService,
        private authenticationService: AuthenticationService,
        private formBuilder: FormBuilder,
        private toastr: ToastrService,
        private router: Router,
        private location: Location
    ) {
        this.subscriptionForm = this.formBuilder.group({
            mainContactName: ['', Validators.required],
            email: ['', [Validators.required, Validators.email]],
            companyName: [''],
            shortDescription: ['', Validators.required],
            longDescription: [''],
            startDate: ['', Validators.required],
            endDate: ['', Validators.required],
            active: [true],
            paymentAmount: ['', [Validators.required, Validators.min(0)]],
            paymentFrequency: ['', Validators.required],
            unitNumber: ['']
        });
    }

    ngOnInit(): void {
        const property = this.getSelectedProperty();
        if (!property) {
            this.errorMessage = 'No property selected. Please return to Property Details.';
            return;
        }

        this.selectedProperty = property;
        this.propertyService.setProperty(property);
        this.loadSubscriptions(property);
    }

    get canManageSubscriptions(): boolean {
        return this.authenticationService.currentUserValue?.role?.includes('ROLE_SUPER') ?? false;
    }

    onAddSubscription(): void {
        this.editingSubscriptionId = null;
        this.creatingSubscription = true;
        this.subscriptionForm.reset({ active: true });
    }

    onEditSubscription(subscription: Subscription): void {
        this.editingSubscriptionId = subscription.id;
        this.creatingSubscription = false;
        this.subscriptionForm.patchValue({
            ...subscription,
            startDate: this.formatDate(subscription.startDate),
            endDate: this.formatDate(subscription.endDate)
        });
    }

    onCancelEdit(): void {
        this.editingSubscriptionId = null;
        this.creatingSubscription = false;
        this.subscriptionForm.reset({ active: true });
    }

    onSaveSubscription(): void {
        if (this.subscriptionForm.invalid || !this.propertyId) {
            this.subscriptionForm.markAllAsTouched();
            return;
        }

        this.saving = true;
        const payload = { ...this.subscriptionForm.value, propertyId: this.propertyId };
        const request = this.editingSubscriptionId
            ? this.subscriptionService.updateSubscription(this.editingSubscriptionId, payload)
            : this.subscriptionService.createSubscription(payload);

        request.subscribe({
            next: (response) => {
                this.toastr.success(response.message || 'Subscription saved.', 'Success');
                this.onCancelEdit();
                this.loadSubscriptions(this.selectedProperty!);
                this.saving = false;
            },
            error: () => {
                this.toastr.error('Failed to save subscription.', 'Error');
                this.saving = false;
            }
        });
    }

    onDeleteSubscription(subscription: Subscription): void {
        if (!confirm(`Remove the subscription for ${subscription.mainContactName}?`)) {
            return;
        }

        this.subscriptionService.deleteSubscription(subscription.id).subscribe({
            next: (response) => {
                this.toastr.success(response.message || 'Subscription removed.', 'Success');
                this.loadSubscriptions(this.selectedProperty!);
            },
            error: () => this.toastr.error('Failed to remove subscription.', 'Error')
        });
    }

    private loadSubscriptions(property: Property): void {
        if (!property || !property.id) {
            this.errorMessage = 'No property selected. Please select a property first.';
            return;
        }

        this.propertyId = Number(property.id);
        this.loading = true;
        this.errorMessage = '';
        this.subscriptionService.getPropertySubscription(this.propertyId).subscribe({
            next: (data) => {
                this.subscriptions = Array.isArray(data) ? data : [data];
                this.loading = false;
                if (!environment.production) {
                    console.log('Subscription loaded: ', data);
                }
            },
            error: (error) => {
                this.loading = false;
                this.subscriptions = [];
                this.errorMessage = 'Failed to load subscription for current property.';
                if (!environment.production) {
                    console.log('Failed to load subscription: ', error);
                }
            }
        });
    }

    private getSelectedProperty(): Property | null {
        const propertyData = localStorage.getItem('property');
        if (!propertyData) {
            return null;
        }

        try {
            return JSON.parse(propertyData) as Property;
        } catch {
            return null;
        }
    }

    private formatDate(value: Date | string): string {
        return value ? new Date(value).toISOString().slice(0, 10) : '';
    }

    goBack(): void {
        this.location.back();
    }

    goHome(): void {
        this.router.navigate(['/home']);
    }
}
